import React, { useState } from "react"
import { ButtonBase, CircularProgress, IconButton, TextField, Theme, Typography, makeStyles } from "@material-ui/core"
import { X } from "react-feather"
import { useTranslation } from "react-i18next"
import { useSearchMessagesQuery } from "../api"
import { MessageViewModel } from "../api/message"
import { useDebounce } from "../../hooks/use-debounce"

// The backend applies the same floor.
const MIN_QUERY_LENGTH = 2
const SEARCH_DEBOUNCE_MS = 500

const dateOf = (message: MessageViewModel) =>
  new Date(message.createdAt).toLocaleDateString([], { day: "numeric", month: "short" })

type Props = {
  chatId: string
  onJumpToMessage: (messageId: string) => void
  onClose: () => void
}

const MessageSearchPanel = ({ chatId, onJumpToMessage, onClose }: Props) => {
  const classes = useStyles()
  const { t } = useTranslation("chat")

  // Apart, so the panel can tell "still typing" from "found nothing".
  const [input, setInput] = useState("")
  const [query, setQuery] = useState("")
  const { debouncedFn: commitQuery } = useDebounce(setQuery, SEARCH_DEBOUNCE_MS)

  const trimmed = query.trim()
  const searchable = trimmed.length >= MIN_QUERY_LENGTH

  const {
    data: results = [],
    isFetching,
    isError,
  } = useSearchMessagesQuery({ chatId, query: trimmed }, { skip: !searchable })

  const settling = input.trim() !== trimmed
  // A failed search must not read as an empty one.
  const showEmpty = searchable && !settling && !isFetching && !isError && results.length === 0

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <TextField
          autoFocus
          fullWidth
          size="small"
          variant="outlined"
          placeholder={t("searchMessagesLabel")}
          value={input}
          onChange={(event) => {
            setInput(event.target.value)
            commitQuery(event.target.value)
          }}
        />

        <IconButton size="small" aria-label={t("closeSearchLabel")} onClick={onClose}>
          <X size={16} />
        </IconButton>
      </div>

      {(isFetching || settling) && searchable && (
        <div className={classes.status}>
          <CircularProgress size={14} />
        </div>
      )}

      {showEmpty && (
        <Typography className={classes.status} color="textSecondary">
          {t("messageSearchPanel.emptyMessage")}
        </Typography>
      )}

      {isError && !isFetching && (
        <Typography className={classes.status} color="error">
          {t("messageSearchPanel.errorMessage")}
        </Typography>
      )}

      {results.length > 0 && (
        <div className={classes.results}>
          {results.map((message) => (
            <ButtonBase key={message.id} className={classes.result} onClick={() => onJumpToMessage(message.id)}>
              <Typography className={classes.sender}>{message.senderName}</Typography>

              <Typography className={classes.date}>{dateOf(message)}</Typography>

              <Typography className={classes.content}>{message.content || t("attachmentLabel")}</Typography>
            </ButtonBase>
          ))}
        </div>
      )}
    </div>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    flexShrink: 0,
    padding: theme.spacing(1, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
  },

  header: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },

  status: {
    padding: theme.spacing(1),
    fontSize: 12,
    textAlign: "center",
  },

  results: {
    maxHeight: theme.spacing(30),
    overflowY: "auto",
  },

  result: {
    width: "100%",
    display: "flex",
    alignItems: "baseline",
    justifyContent: "flex-start",
    gap: theme.spacing(1),
    padding: theme.spacing(0.75),
    borderRadius: 6,

    "&:hover": {
      background: theme.palette.action.hover,
    },
  },

  sender: {
    flexShrink: 0,
    fontSize: 12,
    fontWeight: 600,
  },

  date: {
    flexShrink: 0,
    fontSize: 11,
    color: theme.palette.text.secondary,
  },

  content: {
    minWidth: 0,
    fontSize: 12,
    color: theme.palette.text.secondary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
}))

export default MessageSearchPanel
